import React from "react";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle, Receipt } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { formatCurrency, tooltipStyle } from "./utils";

interface ArAgingChartProps {
  data: { label: string; totalCents: number; count: number }[];
}

const BUCKET_COLORS: Record<string, string> = {
  Current: "#10b981",
  "1-30": "#f59e0b",
  "31-60": "#f97316",
  "61-90": "#f87171",
  "90+": "#dc2626",
};

const getBucketColor = (label: string): string => {
  for (const [key, color] of Object.entries(BUCKET_COLORS)) {
    if (label.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return "#64748b";
};

const ArAgingChart: React.FC<ArAgingChartProps> = ({ data }) => {
  const totalOutstanding = data.reduce((s, d) => s + d.totalCents, 0);
  const chartData = data.map((d) => ({
    label: d.label,
    amount: d.totalCents / 100,
    count: d.count,
    color: getBucketColor(d.label),
  }));

  const isEmpty = !data.length || data.every((d) => d.totalCents === 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-blue-50">
            <Receipt className="h-4 w-4 text-blue-500" />
          </div>
          <h3 className="text-sm font-semibold text-slate-700 m-0 leading-none">
            Accounts Receivable Aging
          </h3>
        </div>
        {totalOutstanding > 0 && (
          <span className="text-sm font-bold text-slate-800">
            {formatCurrency(totalOutstanding)}
          </span>
        )}
      </div>

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center h-64 text-center px-6">
          {/* Decorative checkmark with radiating lines */}
          <div className="relative mb-5">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="h-16 w-16 rounded-2xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)" }}
            >
              <motion.div
                initial={{ scale: 0, rotate: -45 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
              >
                <CheckCircle className="h-8 w-8 text-emerald-500" />
              </motion.div>
            </motion.div>
            {/* Subtle radiating dots */}
            {[0, 60, 120, 180, 240, 300].map((deg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + i * 0.05 }}
                className="absolute w-1.5 h-1.5 rounded-full bg-emerald-200"
                style={{
                  top: `${50 - Math.cos((deg * Math.PI) / 180) * 140}%`,
                  left: `${50 + Math.sin((deg * Math.PI) / 180) * 140}%`,
                  transform: "translate(-50%, -50%)",
                }}
              />
            ))}
          </div>
          <p className="text-sm font-semibold text-slate-600 mb-1">All clear — no outstanding receivables</p>
          <p className="text-xs text-slate-400 max-w-[260px]">
            All invoices are paid up. Aging data will appear here when invoices are issued and outstanding
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 12, fill: "#64748b" }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#64748b" }}
              tickFormatter={(v) => `$${v.toLocaleString()}`}
            />
            <Tooltip
              formatter={(value: number | string | undefined) => [
                formatCurrency(Number(value ?? 0) * 100),
                "Outstanding",
              ]}
              contentStyle={tooltipStyle}
            />
            <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </motion.div>
  );
};

export default ArAgingChart;
